import React, { useCallback, useEffect, useState } from 'react'
import useParams from 'core/hooks/useParams'
import useReactRouter from 'use-react-router'
import { routeManager } from 'core/utils/routes'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createNetwork, createSubnet, listNetworks } from './actions'
import ModalForm from 'core/elements/modal/ModalForm'
import { Route } from 'core/plugins/route'
import TextField from 'core/components/validatedForm/TextField'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import { BasicSubnetFields, AdvancedSubnetFields } from './SubnetFormFields'
import { getIPv6AdditionalFields } from './IPv6AddressConfigurationModePicklist'
import useListAction from 'core/hooks/useListAction'
import AdminStatePicklist from './AdminStatePicklist'
import { getNetworkRequestBody } from './helpers'
import { getHostNetworkConfigs } from '../infrastructure/actions'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import { trackEvent } from 'utils/tracking'
import PicklistField from 'core/components/validatedForm/DropdownField'
import ZonePicklist from './dns/records/ZonePicklist'
import { DNS_ZONE_INFO, MAKE_SHARED_INFO } from './constants'

const useStyles = makeStyles<Theme>((theme) => ({}))

interface Props {
  addRoute: Route
}

export default function CreateNetworkModal({ addRoute }: Props) {
  const { history } = useReactRouter()
  const classes = useStyles()
  const [createdNetworkId, setCreatedNetworkId] = useState(null)
  const [hostConfigs, setHostConfigs] = useState([])
  const isAdminUser = isAdmin()
  const defaultParams = {
    networkType: 'tenant',
    externalNetworkType: 'flat',
    segmentationId: '',
    name: '',
    description: '',
    shared: false,
    adminState: 'Up',
    createSubnet: true,
    ipVersion: 4,
    cidr: '',
    gatewayIp: '',
    disableGateway: false,
    allocationPools: [],
    dnsNameServers: [],
    hostRoutes: [],
    enableDhcp: true,
    dns_publish_fixed_ip: false,
    ipv6AddressConfigurationMode: '',
    enablePortSecurity: true,
    dns_domain: '',
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  useEffect(() => {
    const getConfigs = async () => {
      const configs: any = await getHostNetworkConfigs()
      setHostConfigs(configs)
    }
    getConfigs()
  }, [])

  const { reload } = useListAction(listNetworks, {})
  const { update, updating, error, reset } = useUpdateAction(createNetwork)
  const {
    update: updateSubnet,
    updating: updatingSubnet,
    error: subnetError,
    reset: resetSubnet,
  } = useUpdateAction(createSubnet)

  const createNetworkFn = async (params) => {
    const body = getNetworkRequestBody(params, hostConfigs)
    const { success, response } = await update({ body })
    return { success, response }
  }

  const createSubnetFn = async (params, networkId) => {
    const allocationPools = params.allocationPools.map((entry) => {
      const [start, end] = entry.replace(/\s/g, '').split('-')
      return {
        start,
        end,
      }
    })
    const hostRoutes = params.hostRoutes.map((entry) => {
      const [cidr, nexthop] = entry.replace(/\s/g, '').split(',')
      return {
        destination: cidr,
        nexthop,
      }
    })
    const body = {
      subnet: {
        name: params.subnetName,
        network_id: networkId,
        ip_version: params.ipVersion,
        cidr: params.cidr,
        gateway_ip: params.disableGateway ? null : params.gatewayIp ? params.gatewayIp : undefined,
        allocation_pools: allocationPools.length ? allocationPools : undefined,
        dns_nameservers: params.dnsNameServers.length ? params.dnsNameServers : undefined,
        host_routes: hostRoutes.length ? hostRoutes : undefined,
        enable_dhcp: params.enableDhcp,
        dns_publish_fixed_ip: params.dns_publish_fixed_ip,
        ...getIPv6AdditionalFields(params),
      },
    }
    const { success } = await updateSubnet({ body })
    return { success }
  }

  const submitForm = useCallback(async () => {
    if (createdNetworkId && params.createSubnet) {
      const { success } = await createSubnetFn(params, createdNetworkId)
      if (success) {
        reload(true, true)
        handleClose()
      }
    } else if (createdNetworkId && !params.createSubnet) {
      handleClose()
    } else {
      const { success, response } = await createNetworkFn(params)
      if (success) {
        trackEvent(`Create Network - Finished`)
      }
      if (params.createSubnet) {
        const networkId = response?.id
        setCreatedNetworkId(networkId)
        const { success: subnetSuccess } = await createSubnetFn(params, networkId)
        if (subnetSuccess) {
          trackEvent(`Create Subnet - Finished`)
          reload(true, true)
          handleClose()
        } else {
          return
        }
      }
      if (success) {
        handleClose()
      }
    }
  }, [params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetSubnet()
    setCreatedNetworkId(null)
    if (isAdminUser) {
      history.push(routeManager.routes.openstack.virtualNetworks.path())
    } else {
      history.push(routeManager.routes.openstack.networks.path())
    }
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create Network`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || updatingSubnet}
      error={error || subnetError}
      submitTitle={`Create Network`}
    >
      <>
        <ServiceUnhealthyInfo action="Network Creation" network />
        {/* Only allowing virtual network creation here (tenant networks) */}
        {/* <FormFieldSection title="Network Settings">
          <NetworkTypePicklist
            value={params.networkType}
            onChange={getParamsUpdater('networkType')}
          />
          {params.networkType === 'external' && (
            <ExternalNetworkFields params={params} getParamsUpdater={getParamsUpdater} />
          )}
        </FormFieldSection> */}
        <FormFieldSection title="Network Configuration">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          />
          <AdminStatePicklist value={params.adminState} onChange={getParamsUpdater('adminState')} />
          <PicklistField
            DropdownComponent={ZonePicklist}
            id="zoneId"
            label="DNS Zone"
            onChange={getParamsUpdater('dns_domain')}
            value={params.dns_domain}
            info={DNS_ZONE_INFO}
            setLabelAsValue={true}
          />
          <CheckboxField
            id="enablePortSecurity"
            label="Enable Port Security"
            value={params.enablePortSecurity}
            onChange={getParamsUpdater('enablePortSecurity')}
            info="If checked, newly created ports on this network will have port security enabled by default."
          />
          <CheckboxField
            id="shared"
            label="Make Shared (Across All Tenants)"
            value={params.shared}
            onChange={getParamsUpdater('shared')}
            info={MAKE_SHARED_INFO}
          />
          <CheckboxField
            id="createSubnet"
            label="Create Subnet"
            value={params.createSubnet}
            onChange={getParamsUpdater('createSubnet')}
            info="Create a subnet for this network now."
            tooltipAlign={{
              vertical: 'middle',
              horizontal: 'left',
            }}
          />
        </FormFieldSection>
        {params.createSubnet && (
          <>
            <BasicSubnetFields
              params={params}
              getParamsUpdater={getParamsUpdater}
              inNetworkCreation
            />
            <AdvancedSubnetFields params={params} updateParams={updateParams} />
          </>
        )}
      </>
    </ModalForm>
  )
}
